import styles from 'css/src/components/common/ui/DashboardActionButton.module.scss';

import React from 'react';
import classNames from 'classnames';

import Tooltip from 'js/src/components/common/ui/Tooltip';
import AnimationCirclesIcon from 'js/src/libs/custom_icons/AnimationCirclesIcon';

export enum SvgOptions {
    NEW = 'new',
    CLIP_CREATOR = 'clip-creator',
    ANIMATION = 'animation',
}

export enum ButtonSize {
    SMALL = 'small',
    MEDIUM = 'medium',
}

export type DashboardActionButtonProps = {
    icon?: string;
    title?: string;
    description?: string;
    onClick: () => void;
    className?: string;
    iconClassName?: string;
    descriptionClassName?: string;
    testId?: string;
    disabled?: boolean;
    tooltip?: string;
    svg?: SvgOptions;
    size?: ButtonSize;
};

const DashboardActionButton = React.memo<DashboardActionButtonProps>(
    function DashboardActionButton({
        icon,
        title,
        description,
        onClick,
        className,
        iconClassName,
        descriptionClassName,
        testId,
        disabled = false,
        tooltip,
        svg,
        size = ButtonSize.MEDIUM,
    }) {
        let svgNode: React.ReactNode;
        let svgContainerClassName = '';
        if (svg) {
            switch (svg) {
                case SvgOptions.NEW:
                    svgContainerClassName = 'bg-color-primary-100';
                    svgNode = (
                        <svg
                            width="40"
                            height="40"
                            viewBox="0 0 40 40"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M31.6666 21.6666H21.6666V31.6666H18.3333V21.6666H8.33331V18.3333H18.3333V8.33325H21.6666V18.3333H31.6666V21.6666Z"
                                fill="#5645F5"
                            />
                        </svg>
                    );
                    break;
                case SvgOptions.CLIP_CREATOR:
                    svgContainerClassName = 'bg-color-info-100';
                    svgNode = (
                        <svg
                            width="40"
                            height="40"
                            viewBox="0 0 40 40"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M16.0667 12.7333C16.45 11.9 16.6667 10.9833 16.6667 10C16.6667 6.31666 13.6834 3.33333 10 3.33333C6.31671 3.33333 3.33337 6.31666 3.33337 10C3.33337 13.6833 6.31671 16.6667 10 16.6667C10.9834 16.6667 11.9 16.45 12.7334 16.0667L16.6667 20L12.7334 23.9333C11.9 23.55 10.9834 23.3333 10 23.3333C6.31671 23.3333 3.33337 26.3167 3.33337 30C3.33337 33.6833 6.31671 36.6667 10 36.6667C13.6834 36.6667 16.6667 33.6833 16.6667 30C16.6667 29.0167 16.45 28.1 16.0667 27.2667L20 23.3333L31.6667 35H36.6667V33.3333L16.0667 12.7333ZM10 13.3333C8.16671 13.3333 6.66671 11.85 6.66671 10C6.66671 8.15 8.16671 6.66666 10 6.66666C11.8334 6.66666 13.3334 8.15 13.3334 10C13.3334 11.85 11.8334 13.3333 10 13.3333ZM10 33.3333C8.16671 33.3333 6.66671 31.85 6.66671 30C6.66671 28.15 8.16671 26.6667 10 26.6667C11.8334 26.6667 13.3334 28.15 13.3334 30C13.3334 31.85 11.8334 33.3333 10 33.3333ZM20 20.8333C19.5334 20.8333 19.1667 20.4667 19.1667 20C19.1667 19.5333 19.5334 19.1667 20 19.1667C20.4667 19.1667 20.8334 19.5333 20.8334 20C20.8334 20.4667 20.4667 20.8333 20 20.8333ZM31.6667 4.99999L21.6667 15L25 18.3333L36.6667 6.66666V4.99999H31.6667Z"
                                fill="#00B8C4"
                            />
                        </svg>
                    );
                    break;
                case SvgOptions.ANIMATION:
                    svgContainerClassName = '';
                    svgNode = (
                        <AnimationCirclesIcon
                            strokeColor="#EC2F61"
                            fillColor="#FEEEF2"
                            backgroundColor="#FEEEF2"
                        />
                    );
                    break;
                default:
                    svgNode = null;
                    break;
            }
        }
        const handleClick = React.useCallback(() => {
            if (!disabled) {
                analytics.track('Dashboard Action Button Clicked', {
                    title,
                    testId,
                });
                onClick();
            }
        }, [disabled, onClick, title, testId]);

        const button = (
            <button
                className={classNames(
                    styles['dashboard-cta'],
                    'w-100 card flex-row align-items-center overflow-hidden',
                    {
                        'cursor-pointer': !disabled,
                        'pe-none cursor-not-allowed opacity-50': disabled,
                        [styles['btn-small']]: size === ButtonSize.SMALL,
                        [styles['btn-md']]: size === ButtonSize.MEDIUM,
                    },
                    className,
                )}
                onClick={handleClick}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                data-testid={testId}
            >
                {icon && (
                    <i
                        translate="no"
                        className={classNames('material-symbols-outlined', iconClassName)}
                    >
                        {icon}
                    </i>
                )}
                {svg && (
                    <div className={classNames('p-l5-4 rounded-1', svgContainerClassName)}>
                        {svgNode}
                    </div>
                )}
                <div className="d-flex flex-column ps-l5-12 pe-l5-12 overflow-hidden min-width-0 text-align-left">
                    {title && (
                        <div
                            className={classNames('font-l5 d-flex align-items-center', {
                                'font-l5--small--semibold': size === ButtonSize.SMALL,
                                'font-l5--h6': size === ButtonSize.MEDIUM,
                            })}
                            title={title}
                        >
                            <span className="text-truncate">{title}</span>
                        </div>
                    )}
                    {description && (
                        <div
                            className={classNames(
                                styles['description-text'],
                                'font-l5 font-l5--subtext text-truncate',
                                descriptionClassName,
                                {
                                    'mt-8': title,
                                },
                            )}
                            title={description}
                        >
                            {description}
                        </div>
                    )}
                </div>
            </button>
        );

        if (tooltip) {
            // A disabled button has `pointer-events: none`, so it never fires the hover/focus
            // events the tooltip needs. Wrap it in a div the tooltip can attach to instead, per
            // Bootstrap's disabled-element guidance.
            // https://getbootstrap.com/docs/4.0/components/tooltips/#disabled-elements
            return (
                <Tooltip title={tooltip}>
                    <div className="tooltip-wrapper d-inline-block w-100">{button}</div>
                </Tooltip>
            );
        }

        return button;
    },
);

export default DashboardActionButton;
